/**
 * Footer - Application footer styled to match the GBrands website.
 *
 * Features a dark gray background (#606060) with the GBrands brand name,
 * contact info, service areas, and social/copyright info.
 * Responsive: stacks vertically on mobile, horizontal columns on desktop.
 *
 * Reference: https://www.gbrands.biz footer area
 */
import React from 'react';
import { BrandWordmark } from '../../atoms';

const Footer: React.FC<{ className?: string }> = ({ className }) => {
	const currentYear = new Date().getFullYear();

	return (
		<footer className={`footer bg-[#606060] text-white font-sans ${className ?? ''}`} data-ref="organism-footer">
			{/* --- Main footer content --- */}
			<div className="mx-auto max-w-7xl px-16px tablet:px-24px desktop:px-32px py-32px">
				<div className="footer__columns grid grid-cols-1 tablet:grid-cols-3 gap-32px">

					{/* Column 1: Brand + tagline */}
					<div>
						{/* Light tone — the footer sits on a dark gray panel, so black letters would disappear */}
						<BrandWordmark as="h3" tone="light" size="md" className="footer__brand mb-12px" />
						<p className="text-14px text-gray-300 leading-relaxed">Your Trusted Partner in Commercial &amp; Construction Cleaning Services.</p>
						<p className="text-14px text-gray-300 mt-8px">Work Order &amp; Inspection Management Platform</p>
					</div>

					{/* Column 2: Contact info */}
					<div>
						<h4 className="footer__column-heading font-sans text-16px font-semibold text-brand-primary mb-12px">Get in Touch</h4>
						<div className="space-y-8px text-14px text-gray-300">
							<p>4302 Broadway</p>
							<p>Grove City, Ohio 43123</p>
							<p className="mt-8px">614-707-4445</p>
							<p>info@gbrands.biz</p>
						</div>
					</div>

					{/* Column 3: Service areas */}
					<div>
						<h4 className="footer__column-heading font-sans text-16px font-semibold text-brand-primary mb-12px">Serving</h4>
						<div className="space-y-8px text-14px text-gray-300">
							<p>Central Ohio</p>
							<p>Florida Gulf Coast</p>
						</div>
						{/* Social links row */}
						<div className="footer__social flex items-center mt-16px">
							{/* Facebook */}
							<a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="footer__social-link inline-flex items-center justify-center min-w-44px min-h-44px -my-12px text-gray-400 hover:text-brand-primary transition-colors" aria-label="Facebook">
								<svg className="w-20px h-20px" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
							</a>
							{/* LinkedIn */}
							<a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="footer__social-link inline-flex items-center justify-center min-w-44px min-h-44px -my-12px text-gray-400 hover:text-brand-primary transition-colors" aria-label="LinkedIn">
								<svg className="w-20px h-20px" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
							</a>
							{/* Instagram */}
							<a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="footer__social-link inline-flex items-center justify-center min-w-44px min-h-44px -my-12px text-gray-400 hover:text-brand-primary transition-colors" aria-label="Instagram">
								<svg className="w-20px h-20px" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
							</a>
						</div>
					</div>
				</div>
			</div>

			{/* --- Bottom copyright bar --- */}
			<div className="border-t border-gray-500">
				<div className="mx-auto max-w-7xl px-16px tablet:px-24px desktop:px-32px py-16px">
					<p className="text-center text-12px text-gray-400">&copy; {currentYear} GBrands. All rights reserved.</p>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
